import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useDispatch } from "react-redux";
import { Col, Row } from "react-bootstrap";
import Thumbnail from "src/components/thumbnail";

import { fetchTeamsLearnersReport } from "src/redux/actions/coaching/reports";
import InfiniteScroll from "src/components/infiniteScroll";

import cx from "classnames";
import _ from "lodash";
import styles from "./learners.module.scss";
import { FileService } from "src/utils/file.service";
import Actions from "src/components/actionbar";
import DownloadExcel from "src/components/downloadExcel";
import moment from "moment";
import { CourseReportContext } from "../../../context";
import { getFullName } from "src/utils/utils";

const defaultAssetImage = "/images/defaultIcons/default_asset.jpg";

const LEARNERS_REPORT_SCHEMA = [
  { column: "Name", type: String, value: (row) => row.name },
  { column: "Email", type: String, value: (row) => row.email },
  { column: "Courses Enrolled", type: Number, value: (row) => row.enrolled },
  { column: "Courses Completed", type: Number, value: (row) => row.completed },
  {
    column: "Courses In-Progress",
    type: Number,
    value: (row) => row.inProgress,
  },
  {
    column: "Courses Not Started",
    type: Number,
    value: (row) => row.notStarted,
  },
  { column: "Time Spent", type: String, value: (row) => row.timeSpent },
  {column: "Certifications Earned", type: Number, value: (row) => row.completed},
  { column: "Avg.Score", type: Number, value: (row) => row.averageScore },
];

const LearnersReport: React.FC = () => {
  const dispatch = useDispatch();

  const [query, setQuery] = useState<string | null>("");
  const [sortPreference, setSortPreference] = useState<{
    sortBy: string;
    orderBy: string;
  }>({ sortBy: "enrolled", orderBy: "ASC" });

  const reportContext = useContext(CourseReportContext);

  const { loading, loaded, learners, currentPage, nextPage, hasMoreItems, pagination } =
    useSelector((state: any) => {
      const learnersState = _.get(state.coaching, "reports.teams.learners");
      const loading = _.get(learnersState, "loading");
      const loaded = _.get(learnersState, "loaded");
      const pagination = _.get(learnersState, "pagination");
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      const learners = _.get(learnersState, "items") || [];

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading: loading,
        loaded: loaded,
        learners: learners,
        currentPage: currentPage,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
        pagination: pagination
      };
    }, _.isEqual);

  const fetchState = {
    loading: loading,
    succeeded: loaded,
    error: false,
  };

  const fetchLearnersForPage = (query, page) => {
    const request = {
      query: query,
      page: page,
      limit: 10,
      sort: sortPreference,
      startDate: reportContext.startDate,
      endDate: reportContext.endDate,
    };

    dispatch(fetchTeamsLearnersReport(request));
  };

  const handleLoadMore = () => {
    if (nextPage) {
      fetchLearnersForPage(query, nextPage);
    }
  };

  const handleSort = (sortBy: string, orderBy: string) => {
    setSortPreference({ sortBy, orderBy });
  };

  const renderSort = (orderA: string, orderB: string, sort: string) => {
    return (
      <span
        className={cx(styles.sort, {
          [styles.hideSort]: sortPreference.sortBy !== sort,
        })}
      >
        <i
          className={cx("fa-solid fa-angle-up", {
            [styles.active]:
              sortPreference.orderBy === orderA &&
              sortPreference.sortBy === sort,
          })}
          onClick={() => handleSort(sort, orderA)}
        ></i>
        <i
          className={cx("fa-solid fa-angle-down", {
            [styles.active]:
              sortPreference.orderBy === orderB &&
              sortPreference.sortBy === sort,
          })}
          onClick={() => handleSort(sort, orderB)}
        ></i>
      </span>
    );
  };

  const formatDataForDownload = (data) => {
    if (_.isEmpty(data)) {
      return [];
    }

    return data.map((learner) => ({
      name: getFullName(learner.user),
      email: learner.user.email,
      enrolled: learner.enrolled,
      inProgress: Math.max(learner.inProgress, 0) ?? 0,
      notStarted: Math.max(learner.notStarted, 0) ?? 0,
      completed: Math.max(learner.completed, 0) ?? 0,
      timeSpent: FileService.formatDuration(learner?.duration || 0, "short"),
      certificates: learner.certifications,
      averageScore: Math.round(learner.averageScore ?? 0),
    }));
  };

  const handleQueryChange = (query: any) => {
    setQuery(query || "");
  }

  useEffect(() => {
    fetchLearnersForPage(query, 0);
  }, [query]);  

  useEffect(() => {
    fetchLearnersForPage(query, 0);
  }, [sortPreference]);

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h3>learner insights: progress, performance & participation</h3>
          <span className={styles.description}>
            your team&apos;s journey, tracked and celebrated
          </span>
        </div>
        <div className={styles.actions}>
          <Actions.Search query={query} onSearch={handleQueryChange} />
          {_.size(learners) > 0 && (
            <div className={styles.downloadButton}>
              <DownloadExcel
                title="Learner Insights Report"
                fileName="LearnerInsightsReport"
                schema={LEARNERS_REPORT_SCHEMA}
                data={formatDataForDownload(learners)}
                fetchState={fetchState}
                pagination={pagination}
                fetchDataForPage={(page) => fetchLearnersForPage("", page)}
                invalidateData={() => {}}
              />
            </div>
          )}
        </div>
      </div>

      <div className={cx(styles.learningTable)}>
        {!loading && _.size(learners) > 0 && (
          <Row className={styles.tableHeader}>
            <Col lg={2} className={styles.heading}>
              learners
            </Col>
            <Col lg={2} className={styles.heading}>
              courses enrolled
              {renderSort("ASC", "DESC", "enrolled")}
            </Col>
            <Col lg={2} className={styles.heading}>
              courses completed
              {renderSort("ASC", "DESC", "completed")}
            </Col>
            <Col lg={2} className={styles.heading}>
              courses in-progress
              {renderSort("ASC", "DESC", "inProgress")}
            </Col>
            <Col lg={1} className={styles.heading}>
              courses not started
              {renderSort("ASC", "DESC", "notStarted")}
            </Col>
            <Col lg={1} className={styles.heading}>
              time spent
              {renderSort("ASC", "DESC", "duration")}
            </Col>
            <Col lg={1} className={styles.heading}>
              certifications earned
              {renderSort("ASC", "DESC", "certificates")}
            </Col>
            <Col lg={1} className={styles.heading}>
              average score
              {renderSort("ASC", "DESC", "averageScore")}
            </Col>
          </Row>
        )}
        <InfiniteScroll
          className={styles.learnersList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMore}
          loader={() => {
            return _.times(_.size(learners) === 0 ? 4 : 1, (index) => (
              <PlaceholderLearner key={index} />
            ));
          }}
        >
          {_.size(learners) === 0 && !loading && (
            <span className={styles.noRecordsFound}>No results found</span>
          )}

          {_.size(learners) > 0 && (
            <div className={styles.tableBody}>
              {learners.map((item, index) => {
                return (
                  <Row key={index} className={styles.tableRow}>
                    <Col lg={2} className={styles.info}>
                      <Thumbnail
                        media={item.user?.icon}
                        defaultImage={defaultAssetImage}
                        className={styles.thumbnail}
                      />
                      <div>
                        <h4 className={styles.title}>
                          {item.user.firstName || "Unknown User"}
                        </h4>
                      </div>
                    </Col>
                    <Col
                      lg={2}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {item.enrolled} learnings
                    </Col>
                    <Col
                      lg={2}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {item.completed}
                    </Col>
                    <Col
                      lg={2}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {item.inProgress}
                    </Col>
                    <Col
                      lg={1}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {item.notStarted}
                    </Col>
                    <Col
                      lg={1}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {FileService.formatDuration(item?.duration || 0, "short")}
                    </Col>
                    <Col
                      lg={1}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {item.certifications}
                    </Col>
                    <Col
                      lg={1}
                      className={cx(styles.columnHeading, styles.assignedUsers)}
                    >
                      {Math.min(Number(((item?.averageScore || 0) * 100).toFixed(2)), 100)}%
                    </Col>
                  </Row>
                );
              })}
            </div>
          )}
        </InfiniteScroll>
      </div>
    </section>
  );
};

export default LearnersReport;

const PlaceholderLearner = () => {
  return (
    <Row className={cx(styles.tableRow, styles.placeholder)}>
      <Col lg={2} className={styles.info}>
        <div className={cx(styles.thumbnail, styles.block)}></div>
        <div className={styles.details}>
          <h4 className={cx(styles.title, styles.block)}></h4>
        </div>
      </Col>
      <Col lg={2} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
      <Col lg={2} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
      <Col lg={2} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
      <Col lg={1} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
      <Col lg={1} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
      <Col lg={1} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
      <Col lg={1} className={styles.columnHeading}>
        <div className={styles.block}></div>
      </Col>
    </Row>
  );
};
